import { ProblemSchema } from '@nlc/contracts';
import type { z } from 'zod';

const configuredApiBase: unknown = import.meta.env.VITE_API_BASE_URL;
const API_BASE = typeof configuredApiBase === 'string' ? configuredApiBase : '/api/v1';

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(`${API_BASE}${path}`, { ...init, credentials: 'include', headers });
  if (!response.ok) {
    const parsed = ProblemSchema.safeParse(await response.json().catch(() => null));
    throw new ApiError(
      response.status,
      parsed.success ? parsed.data.code : 'request.failed',
      parsed.success
        ? (parsed.data.detail ?? parsed.data.title)
        : 'The request could not be completed.',
    );
  }
  if (response.status === 204) return undefined as T;
  return schema.parse(await response.json());
}

export function queryString(values: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}
